import React from 'react';
import {
  Dialog,
  Box,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Psychology as PsychologyIcon,
  TuneRounded as ControlIcon,
} from '@mui/icons-material';
import { Txt } from './design-system';
import { MONO, PanelButton } from './instrument';

interface DataDisclosureDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const bulletItems = [
  {
    icon: <StorageIcon sx={{ fontSize: 20, color: '#00E0FF', filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.4))' }} />,
    text: 'We collect your nutrition, health and fitness data to give you your scores and suggestions.',
  },
  {
    icon: <PsychologyIcon sx={{ fontSize: 20, color: '#CE93D8', filter: 'drop-shadow(0 0 6px rgba(206,147,216,0.4))' }} />,
    text: 'Your data is sent to AI services from Google and OpenAI, which produce automated estimates and suggestions.',
  },
  {
    icon: <ControlIcon sx={{ fontSize: 20, color: '#00E676', filter: 'drop-shadow(0 0 6px rgba(0,230,118,0.4))' }} />,
    text: 'You control your data. See it, export it or delete it any time in Settings.',
  },
];

export const DataDisclosureDialog: React.FC<DataDisclosureDialogProps> = ({
  open,
  onAccept,
  onDecline,
}) => {
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{
        sx: {
          background: 'var(--bg-card)',
          border: '1px solid rgba(0,224,255,0.4)',
          borderRadius: '12px !important',
          maxWidth: 420,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 16,
            right: 16,
            height: '2px',
            background: `#00E0FF`, boxShadow: `0 0 12px #00E0FF`,
          },
        },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Logo + title */}
        <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <img
            src="/FuelTron.png"
            alt="FuelTron"
            style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid rgba(0,224,255,0.4)', boxShadow: '0 0 10px rgba(0,224,255,0.3)' }}
          />
          <Txt
            component="h2"
            sx={{
              m: 0,
              fontFamily: MONO,
              fontSize: '0.8rem',
              fontWeight: 800,
              color: '#00E0FF',
              textTransform: 'uppercase',
              letterSpacing: 2,
              textShadow: '0 0 10px rgba(0,224,255,0.4)',
            }}
          >
            Your data, your control
          </Txt>
        </Box>

        {/* Bullet points */}
        <Box sx={{ textAlign: 'left', mb: 2 }}>
          {bulletItems.map((item, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                py: 1,
                borderBottom: i < bulletItems.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
              }}
            >
              <Box sx={{ flexShrink: 0, mt: '1px', display: 'flex', '& svg': { fontSize: 18 } }}>{item.icon}</Box>
              <Txt sx={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.8rem', lineHeight: 1.45 }}>
                {item.text}
              </Txt>
            </Box>
          ))}
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
          <PanelButton variant="quiet" onClick={onDecline}>
            Decline
          </PanelButton>
          <PanelButton variant="primary" onClick={onAccept} sx={{ px: 2.5 }}>
            Accept and continue
          </PanelButton>
        </Box>

        {/* Medical disclaimer */}
        <Txt sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem', mt: 1.5, fontFamily: MONO, lineHeight: 1.5 }}>
          FuelTron is not a medical device. It does not diagnose, treat, cure or prevent any disease. For information only.
        </Txt>

        {/* Fine print */}
        <Txt sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.6rem', mt: 0.5, fontFamily: MONO }}>
          Full details are in the Privacy Policy, in Settings.
        </Txt>
      </Box>
    </Dialog>
  );
};
